import { BookOpen, Hourglass, Play } from 'lucide-react'
import { useState } from 'react'

import { clockLabel, spokenLength, storyPartCount, storySeconds, type StoryRow } from '@/api/stories'
import { cn } from '@/components/ui/utils'

/**
 * A story's card picture, video-style: its first part's stage with everyone in their opening
 * places (`GET /api/stories/<uid>/thumb.webp`, drawn by the server from the bundle), the length in
 * the corner the way a video player writes it ("4:13") and, for a longer story, how many parts.
 *
 * A story that is not made yet (or whose picture cannot be loaded) shows a quiet placeholder of the
 * same size, so a grid of cards never changes height while pictures arrive. The picture is
 * decorative (`alt=""`): the title sits right next to it and the length is read out in words.
 */

export type StoryThumbStory = Pick<StoryRow, 'uid' | 'title' | 'status' | 'thumb_url' | 'duration_s' | 'runtime_s' | 'scenes' | 'parts'>

export interface StoryThumbProps {
  story: StoryThumbStory
  /** Show a play glyph over the picture (the card links to the player). */
  playable?: boolean
  /** `hero`: the story page's big picture (larger corner labels). */
  size?: 'card' | 'hero'
  /** Label a longer story's parts on the picture (off for a small row picture: its text says it). */
  showParts?: boolean
  className?: string
}

export function StoryThumb({ story, playable = false, size = 'card', showParts = true, className }: StoryThumbProps) {
  const ready = story.status === 'ready'
  const src = ready ? (story.thumb_url ?? null) : null
  // Keyed by `src`: a new picture (a re-bake that moved someone) gets a fresh chance to load.
  const [failed, setFailed] = useState<string | null>(null)
  const seconds = ready ? storySeconds(story) : null
  const clock = clockLabel(seconds)
  const parts = ready && showParts ? storyPartCount(story) : 0
  const big = size === 'hero'
  const label = cn(
    'pointer-events-none absolute rounded-md bg-black/70 font-semibold tabular-nums text-white shadow-sm',
    big ? 'bottom-3 px-2 py-1 text-sm' : 'bottom-2 px-1.5 py-0.5 text-xs',
  )

  return (
    <div className={cn('relative aspect-video w-full overflow-hidden bg-paper-2', className)} data-testid="story-thumb">
      {src && failed !== src ? (
        <img
          key={src}
          src={src}
          alt=""
          // The story page's picture is its first thing on screen: fetch it now, not lazily.
          loading={big ? 'eager' : 'lazy'}
          fetchPriority={big ? 'high' : 'auto'}
          decoding="async"
          draggable={false}
          width={640}
          height={360}
          onError={() => setFailed(src)}
          className="size-full object-cover"
        />
      ) : (
        <span className="flex size-full flex-col items-center justify-center gap-2 text-ink-3" data-testid="story-thumb-placeholder">
          {story.status === 'generating' ? <Hourglass className="size-8" aria-hidden /> : <BookOpen className="size-8" aria-hidden />}
          {story.status === 'generating' ? <span className="text-xs">Being made…</span> : null}
        </span>
      )}

      {playable && src ? (
        <span
          className={cn(
            'pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-opacity duration-150',
            'group-hover:bg-black/15 group-hover:opacity-100 group-focus-visible:opacity-100 motion-reduce:transition-none',
          )}
          aria-hidden
        >
          <span className={cn('flex items-center justify-center rounded-full bg-white/90 text-ink shadow-pop', big ? 'size-16' : 'size-12')}>
            <Play className={cn('translate-x-0.5 fill-current', big ? 'size-7' : 'size-5')} />
          </span>
        </span>
      ) : null}

      {parts > 1 ? (
        <span className={cn(label, big ? 'left-3' : 'left-2')} data-testid="story-parts">
          {parts} parts
        </span>
      ) : null}
      {clock ? (
        <span className={cn(label, big ? 'right-3' : 'right-2')} title={spokenLength(seconds) ?? undefined} data-testid="story-duration">
          <span aria-hidden>{clock}</span>
          <span className="sr-only">{spokenLength(seconds)}</span>
        </span>
      ) : null}
    </div>
  )
}
